/**
 * Bundle size optimization utilities
 */

/**
 * Tree-shaking helper for conditional imports
 */
export function conditionalImport<T>(
  condition: boolean,
  importFn: () => Promise<T>
): Promise<T | null> {
  return condition ? importFn() : Promise.resolve(null);
}

/**
 * Lazy import with size tracking
 */
export function createSizeTrackedImport<T>(
  importFn: () => Promise<{ default: T }>,
  estimatedSize?: number
) {
  return async (): Promise<{ default: T; size?: number }> => {
    const startTime = performance.now();
    const module = await importFn();
    const loadTime = performance.now() - startTime;
    
    // Log bundle loading metrics
    if (process.env.NODE_ENV === 'development') {
      console.log(`Bundle loaded in ${loadTime.toFixed(2)}ms`, {
        estimatedSize: estimatedSize ? `${estimatedSize}KB` : 'unknown',
        actualLoadTime: `${loadTime.toFixed(2)}ms`
      });
    }
    
    return {
      ...module,
      size: estimatedSize
    };
  };
}

/**
 * Polyfill loader for modern features
 */
export async function loadPolyfillsIfNeeded(): Promise<void> {
  // For now, we'll skip polyfills in the build to avoid dependency issues
  // In a real implementation, these would be installed as optional dependencies
  
  if (process.env.NODE_ENV === 'development') {
    console.log('Polyfill loading skipped in current build configuration');
  }
  
  // Future implementation would check for and load polyfills:
  // - intersection-observer
  // - @juggle/resize-observer  
  // - web-animations-js
}

/**
 * Feature detection and conditional loading
 */
export const FeatureDetection = {
  // Check if user prefers reduced motion
  prefersReducedMotion: () => 
    window.matchMedia('(prefers-reduced-motion: reduce)').matches,

  // Check if device has touch capability
  hasTouchSupport: () => 
    'ontouchstart' in window || navigator.maxTouchPoints > 0,

  // Check if device is mobile
  isMobile: () => 
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),

  // Check if connection is slow
  isSlowConnection: () => {
    const connection = (navigator as any).connection;
    return connection && (
      connection.effectiveType === 'slow-2g' || 
      connection.effectiveType === '2g' ||
      connection.saveData
    );
  },

  // Check if user has sufficient memory
  hasLowMemory: () => {
    const memory = (navigator as any).deviceMemory;
    return memory && memory < 4; // Less than 4GB RAM
  },

  // Check if device supports WebP
  supportsWebP: () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
};

/**
 * Adaptive loading based on device capabilities
 */
export class AdaptiveLoader {
  private static instance: AdaptiveLoader;
  private loadingStrategy: 'aggressive' | 'conservative' | 'minimal';

  constructor() {
    this.loadingStrategy = this.determineLoadingStrategy();
  }

  static getInstance(): AdaptiveLoader {
    if (!AdaptiveLoader.instance) {
      AdaptiveLoader.instance = new AdaptiveLoader();
    }
    return AdaptiveLoader.instance;
  }

  private determineLoadingStrategy(): 'aggressive' | 'conservative' | 'minimal' {
    const isSlowConnection = FeatureDetection.isSlowConnection();
    const hasLowMemory = FeatureDetection.hasLowMemory();
    const isMobile = FeatureDetection.isMobile();

    if (isSlowConnection || hasLowMemory) {
      return 'minimal';
    } else if (isMobile) {
      return 'conservative';
    } else {
      return 'aggressive';
    }
  }

  shouldLoadFeature(featureName: string, priority: 'low' | 'medium' | 'high'): boolean {
    switch (this.loadingStrategy) {
      case 'minimal':
        return priority === 'high';
      case 'conservative':
        return priority === 'high' || priority === 'medium';
      case 'aggressive':
        return true;
      default:
        return priority === 'high';
    }
  }

  getImageFormat(): 'webp' | 'jpg' {
    return FeatureDetection.supportsWebP() ? 'webp' : 'jpg';
  }

  getChunkSize(): number {
    switch (this.loadingStrategy) {
      case 'minimal':
        return 50; // 50KB chunks
      case 'conservative':
        return 100; // 100KB chunks
      case 'aggressive':
        return 200; // 200KB chunks
      default:
        return 100;
    }
  }
}

/**
 * Bundle analyzer for development
 */
export const BundleAnalyzer = {
  // Track loaded chunks
  loadedChunks: new Set<string>(),
  
  // Track chunk sizes
  chunkSizes: new Map<string, number>(),
  
  // Track loading times
  loadingTimes: new Map<string, number>(),

  trackChunkLoad(chunkName: string, size?: number, loadTime?: number): void {
    this.loadedChunks.add(chunkName);
    
    if (size) {
      this.chunkSizes.set(chunkName, size);
    }
    
    if (loadTime) {
      this.loadingTimes.set(chunkName, loadTime);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('Bundle Analysis:', {
        totalChunks: this.loadedChunks.size,
        totalSize: Array.from(this.chunkSizes.values()).reduce((a, b) => a + b, 0),
        averageLoadTime: Array.from(this.loadingTimes.values()).reduce((a, b) => a + b, 0) / this.loadingTimes.size
      });
    }
  },

  getReport(): {
    totalChunks: number;
    totalSize: number;
    averageLoadTime: number;
    chunks: Array<{ name: string; size?: number; loadTime?: number }>;
  } {
    const chunks = Array.from(this.loadedChunks).map(name => ({
      name,
      size: this.chunkSizes.get(name),
      loadTime: this.loadingTimes.get(name)
    }));

    return {
      totalChunks: this.loadedChunks.size,
      totalSize: Array.from(this.chunkSizes.values()).reduce((a, b) => a + b, 0),
      averageLoadTime: Array.from(this.loadingTimes.values()).reduce((a, b) => a + b, 0) / this.loadingTimes.size || 0,
      chunks
    };
  }
};

/**
 * Preload critical resources
 */
export function preloadCriticalResources(): void {
  // Preload critical fonts
  const criticalFonts = [
    '/fonts/inter-regular.woff2',
    '/fonts/inter-medium.woff2'
  ];

  criticalFonts.forEach(font => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.type = 'font/woff2';
    link.crossOrigin = 'anonymous';
    link.href = font;
    document.head.appendChild(link);
  });

  // Preload critical images
  const criticalImages = [
    '/images/logo.svg',
    '/images/hero-bg.webp'
  ];

  criticalImages.forEach(image => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image;
    document.head.appendChild(link);
  });
}